import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../app/store/store';
import {
  fetchHelpdeskSettings,
  updateHelpdeskSettings,
} from '../../../entities/helpdesk-settings/model';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import type { EmailTestResponse } from '../../../types/helpdeskSettingsInterfaces';

/* ── Модалка редактирования ── */
interface EmailEditModalProps {
  type: 'imap' | 'smtp';
  settings: any;
  onClose: () => void;
  onSave: (data: Record<string, any>) => void;
}

const EmailEditModal: React.FC<EmailEditModalProps> = ({ type, settings, onClose, onSave }) => {
  const isImap = type === 'imap';
  // Каждый initialState читает СВОЁ поле — раньше тут был баг,
  // и `user` для SMTP-ветки тоже подтягивал email_imap_user, поэтому
  // SMTP-логин никогда не сохранялся.
  const [host, setHost] = useState(isImap ? settings.email_imap_host : settings.email_smtp_host);
  const [port, setPort] = useState(isImap ? settings.email_imap_port : settings.email_smtp_port);
  const [ssl, setSsl] = useState(isImap ? settings.email_imap_ssl : settings.email_smtp_ssl);
  const [user, setUser] = useState(isImap ? settings.email_imap_user : settings.email_smtp_user);
  const [password, setPassword] = useState('');
  const [fromAddress, setFromAddress] = useState(settings.email_from_address || '');
  const [fromName, setFromName] = useState(settings.email_from_name || '');
  const [pollInterval, setPollInterval] = useState(settings.email_imap_poll_interval || 2);

  const passwordIsSet = isImap
    ? settings.email_imap_password_set
    : settings.email_smtp_password_set;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: Record<string, any> = {};
    if (isImap) {
      data.email_imap_host = host;
      data.email_imap_port = port;
      data.email_imap_ssl = ssl;
      data.email_imap_user = user;
      if (password) data.email_imap_password = password;
      data.email_imap_poll_interval = pollInterval;
    } else {
      data.email_smtp_host = host;
      data.email_smtp_port = port;
      data.email_smtp_ssl = ssl;
      data.email_smtp_user = user;
      if (password) data.email_smtp_password = password;
      data.email_from_address = fromAddress;
      data.email_from_name = fromName;
    }
    onSave(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            {isImap ? 'Настройки IMAP' : 'Настройки SMTP'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Хост</label>
            <input value={host} onChange={(e) => setHost(e.target.value)}
              placeholder={isImap ? 'imap.company.com' : 'smtp.company.com'}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Порт</label>
              <input type="number" value={port} onChange={(e) => setPort(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SSL</label>
              <select value={ssl ? 'yes' : 'no'} onChange={(e) => setSsl(e.target.value === 'yes')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400">
                <option value="yes">Да</option>
                <option value="no">Нет</option>
              </select>
            </div>
          </div>

          {/* Логин/пароль одинаково нужны и IMAP, и SMTP — раньше для
              SMTP их просто не было в форме, и креды никогда не
              сохранялись. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Пользователь</label>
            <input value={user} onChange={(e) => setUser(e.target.value)}
              placeholder="support@company.com"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Пароль {passwordIsSet && <span className="text-gray-400 text-xs">(установлен)</span>}
            </label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={passwordIsSet ? '••••••••' : 'Введите пароль'}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
            {!isImap && !passwordIsSet && (
              <p className="text-xs text-gray-400 mt-1">
                Если оставить пустым, для отправки будет использован IMAP-пароль.
              </p>
            )}
          </div>

          {isImap && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Интервал проверки (мин)</label>
              <input type="number" value={pollInterval} min={1} max={60}
                onChange={(e) => setPollInterval(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
            </div>
          )}

          {!isImap && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email отправителя</label>
                <input value={fromAddress} onChange={(e) => setFromAddress(e.target.value)}
                  placeholder="support@company.com"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Имя отправителя</label>
                <input value={fromName} onChange={(e) => setFromName(e.target.value)}
                  placeholder="Техническая поддержка"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Отмена
            </button>
            <button type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022]">
              Сохранить
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Строка информации ── */
const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-[#2d2c2c]">{value}</span>
  </div>
);

/* ── Основной экран ── */
const EmailChannelSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { settings, status } = useSelector((state: RootState) => state.helpdeskSettings);

  const [editModal, setEditModal] = useState<'imap' | 'smtp' | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<EmailTestResponse | null>(null);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchHelpdeskSettings());
  }, [dispatch, status]);

  const handleSave = useCallback(
    async (data: Record<string, any>) => {
      await dispatch(updateHelpdeskSettings(data));
      setEditModal(null);
    },
    [dispatch],
  );

  const handleTest = useCallback(async (type: 'imap' | 'smtp' | 'both') => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await helpdeskSettingsApi.testEmailConnection(type);
      setTestResult(response.data);
      // Refresh settings to update connected status
      dispatch(fetchHelpdeskSettings());
    } catch {
      setTestResult({ imap: { success: false, message: 'Ошибка подключения' } });
    } finally {
      setTesting(false);
    }
  }, [dispatch]);

  if (status === 'loading' || !settings) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          <div className="h-24 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Входящий email (IMAP) */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-[#2d2c2c]">Входящий email (IMAP)</h2>
            <span className={`flex items-center gap-1.5 text-xs font-medium ${
              settings.email_imap_connected ? 'text-green-600' : 'text-gray-400'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                settings.email_imap_connected ? 'bg-green-500' : 'bg-gray-300'
              }`} />
              {settings.email_imap_connected ? 'Подключен' : 'Не подключен'}
            </span>
          </div>
          <button onClick={() => setEditModal('imap')}
            className="px-4 py-2 text-sm font-medium text-[#2d2c2c] border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Изменить настройки
          </button>
        </div>

        <div>
          <InfoRow label="Адрес почтового ящика" value={settings.email_imap_user || '—'} />
          <InfoRow label="IMAP-сервер" value={settings.email_imap_host ? `${settings.email_imap_host}:${settings.email_imap_port}` : '—'} />
          <InfoRow label="Интервал проверки" value={`каждые ${settings.email_imap_poll_interval} минуты`} />
        </div>
      </div>

      {/* Исходящий email (SMTP) */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-[#2d2c2c]">Исходящий email (SMTP)</h2>
            <span className={`flex items-center gap-1.5 text-xs font-medium ${
              settings.email_smtp_connected ? 'text-green-600' : 'text-gray-400'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                settings.email_smtp_connected ? 'bg-green-500' : 'bg-gray-300'
              }`} />
              {settings.email_smtp_connected ? 'Подключен' : 'Не подключен'}
            </span>
          </div>
          <button onClick={() => setEditModal('smtp')}
            className="px-4 py-2 text-sm font-medium text-[#2d2c2c] border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Изменить настройки
          </button>
        </div>

        <div>
          <InfoRow label="Отправитель" value={settings.email_from_address || '—'} />
          <InfoRow label="Имя отправителя" value={settings.email_from_name || '—'} />
          <InfoRow
            label="SMTP-логин"
            value={
              settings.email_smtp_user ||
              (settings.email_imap_user
                ? `${settings.email_imap_user} (используется IMAP-учётка)`
                : '—')
            }
          />
          <InfoRow label="SMTP-сервер" value={settings.email_smtp_host ? `${settings.email_smtp_host}:${settings.email_smtp_port}` : '—'} />
        </div>
      </div>

      {/* Кнопка тестирования */}
      <div className="flex items-center gap-4">
        <button onClick={() => handleTest('both')} disabled={testing}
          className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] disabled:opacity-50 transition-colors">
          {testing ? 'Проверяем...' : 'Проверить подключение'}
        </button>

        {testResult && (
          <div className="flex items-center gap-4 text-sm">
            {testResult.imap && (
              <span className={testResult.imap.success ? 'text-green-600' : 'text-red-500'}>
                IMAP: {testResult.imap.message}
              </span>
            )}
            {testResult.smtp && (
              <span className={testResult.smtp.success ? 'text-green-600' : 'text-red-500'}>
                SMTP: {testResult.smtp.message}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Модалки */}
      {editModal && (
        <EmailEditModal type={editModal} settings={settings}
          onClose={() => setEditModal(null)} onSave={handleSave} />
      )}
    </div>
  );
};

export default EmailChannelSettings;
